import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Spin, Empty } from 'antd';
import { PrinterOutlined, FilePdfOutlined } from '@ant-design/icons';
import { get_final_batch_report_json } from '@/utils/api';
import { rt_list } from '../Configurations/reportTemplateApi';
import { formatDateTimeSeconds } from '@/utils/datetime';
import { useMbmrAlias } from '@/hooks/useAppSetting';

const resolveUrl = (url) => url || '';

const SYSTEM_TEMPLATE = {
  header: { title: '', bgColor: '#204448', textColor: '#ffffff', fontSize: 14, logo: '' },
  footer: {
    text: 'Confidential — For Internal Use Only',
    bgColor: '#f3f4f6',
    textColor: '#374151',
    fontSize: 10,
  },
};

const normalizeTemplate = (raw) => ({
  fontSize: raw.base_font_size || 12,
  header: {
    title: raw.header?.title || '',
    bgColor: raw.header?.bg_color || '#204448',
    textColor: raw.header?.text_color || '#ffffff',
    fontSize: raw.header?.font_size || 14,
    logo: resolveUrl(raw.header?.logo_url || ''),
  },
  footer: {
    text: raw.footer?.text || 'Confidential — For Internal Use Only',
    bgColor: raw.footer?.bg_color || '#f3f4f6',
    textColor: raw.footer?.text_color || '#374151',
    fontSize: raw.footer?.font_size || 10,
  },
});

/**
 * Detailed Batch Manufacturing Report — DATA-DRIVEN.
 *
 * Fetches the report JSON from get_final_batch_report_json and renders it in the
 * UI. Styling mirrors the backend HTML/PDF report (same colors, card sections,
 * blue table headers, teal section bars, status pills, deviation-severity
 * colors) so it looks identical to the backend report — but it is built here
 * from structured data (sortable/extendable later).
 *
 * Props: batchNumber (string)
 */

const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const num = (v, d = 2) =>
  v === null || v === undefined || v === '' || isNaN(Number(v)) ? '—' : Number(v).toFixed(d);
// Format a backend timestamp for display. The report APIs now emit UTC, and this
// helper (from datetime.js) converts UTC → the app display timezone. Values that
// aren't parseable datetimes fall back to the placeholder.
const dt = (v) => (v === null || v === undefined || v === '' ? '—' : formatDateTimeSeconds(v));

const sevClass = (sev) => {
  switch ((sev || '').toUpperCase()) {
    case 'CRITICAL':
      return 'deviation-critical';
    case 'MODERATE':
      return 'deviation-moderate';
    case 'MINOR':
      return 'deviation-minor';
    default:
      return '';
  }
};
const statusClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('complet')) return 'completed';
  if (s.includes('progress')) return 'in-progress';
  if (s.includes('reject') || s.includes('terminat')) return 'rejected';
  return 'pending';
};
const StatusPill = ({ status }) =>
  status ? <span className={`status ${statusClass(status)}`}>{status}</span> : <>—</>;
const StagePill = ({ stage }) => {
  if (!stage) return <>—</>;
  const s = (stage || '').toUpperCase();
  const cls = s === 'PRE' ? 'pill-action' : s === 'POST' ? 'pill-muted' : 'pill-dev';
  return <span className={`pill ${cls}`}>{stage}</span>;
};

const BatchDetailedReport = ({ batchNumber }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const [tpl, setTpl] = useState(SYSTEM_TEMPLATE);
  const companyLogo = useSelector((s) => s.company_profile?.company_logo || '');
  const mbmrAlias = useMbmrAlias();

  useEffect(() => {
    rt_list((res) => {
      const list = res?.data?.data;
      if (Array.isArray(list) && list.length > 0) {
        const def = list.find((t) => t.is_default) || list[0];
        setTpl(normalizeTemplate(def));
      }
    });
  }, []);

  useEffect(() => {
    if (!batchNumber) return;
    let active = true;
    setLoading(true);
    setError('');
    setData(null);
    get_final_batch_report_json(
      { batch_number: batchNumber },
      (res) => {
        if (!active) return;
        if (res?.data?.status === 'success' && res?.data?.data) {
          setData(res.data.data);
        } else {
          setError(res?.data?.message || 'No report data available');
        }
        setLoading(false);
      },
      () => {
        if (!active) return;
        setError('Failed to load report');
        setLoading(false);
      }
    );
    return () => {
      active = false;
    };
  }, [batchNumber]);

  const handlePrint = () => {
    const node = rootRef.current;
    if (!node) return;

    const win = window.open('', '_blank');
    if (!win) {
      // Popup blocked — fall back to in-page print so user at least gets something
      window.print();
      return;
    }

    // Print-window-only override: pin the report to the exact A4 content width
    // (186mm) at ALL times in this popup — not just inside @media print. Chrome's
    // "Save as PDF" lays the page out using the popup's SCREEN width, so if the
    // report is width:100% of a wide popup, the fixed-layout tables compute wider
    // than A4 and overflow. Forcing 186mm here makes the tables fit the page in
    // both "Print" and "Save as PDF", matching the on-screen preview proportions.
    const printWindowStyle = `<style>
        html, body { margin: 0; padding: 0; background: #fff; }
        body { width: 186mm; margin: 0 auto; }
        .bmr-report { width: 186mm !important; max-width: 186mm !important; margin: 0 auto !important; padding: 0 !important; }
        .bmr-report table { width: 100% !important; max-width: 186mm !important; table-layout: fixed !important; }
      </style>`;

    win.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8">` +
        `<base href="${window.location.origin}/">` +
        `<title>${dash(data?.header?.batch_number)}</title>` +
        `${getReportStyle()}${printWindowStyle}</head><body>${node.outerHTML}</body></html>`
    );
    win.document.close();
    win.focus();

    // Wait for every image to finish loading (or fail) before triggering print.
    // A fixed timeout is unreliable — logo over proxy can take >400ms on slow networks.
    const triggerPrint = () => {
      win.print();
      // Don't auto-close — let the user decide after the print dialog.
    };

    const imgs = Array.from(win.document.images);
    if (imgs.length === 0) {
      triggerPrint();
      return;
    }

    let pending = imgs.length;
    const onSettled = () => {
      pending -= 1;
      if (pending === 0) triggerPrint();
    };

    imgs.forEach((img) => {
      if (img.complete) {
        // Already cached / already loaded
        onSettled();
      } else {
        img.addEventListener('load', onSettled);
        img.addEventListener('error', onSettled); // failed img still counts as settled
      }
    });
  };

  // "Download PDF" used to call the backend's get_final_batch_pdf_report,
  // which is confirmed (per backend team) to intentionally return the same
  // structured JSON as get_final_batch_report_json rather than a base64 PDF —
  // so this can't be fixed by changing what we expect from that endpoint.
  // Instead, generate the PDF entirely client-side by reusing the exact same
  // print-window mechanism as "Print / Download" below: it renders this same
  // report's HTML at fixed A4 width and calls the browser's native print,
  // whose "Save as PDF" destination produces the file — no backend involved.
  const handleDownloadPdf = () => {
    if (!batchNumber) return;
    handlePrint();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" tip="Generating report…" />
      </div>
    );
  }
  if (error || !data) {
    return <Empty description={error || 'No report'} style={{ padding: 60 }} />;
  }

  const {
    header = {},
    process_time_details = [],
    process_time_totals = {},
    bmr_approvals = {},
    operation_summary = [],
    processes = [],
    material_balance_summary = {},
  } = data;

  return (
    <div>
      {/* getReportStyle() already returns a full <style>...</style> block, so
          inject it as raw HTML. Wrapping it in another <style> made the browser
          treat the inner <style> tag as CSS text — so the on-screen preview CSS
          never applied (only the print window worked). This makes preview match
          the printed/PDF output exactly. */}
      <div dangerouslySetInnerHTML={{ __html: getReportStyle() }} />
      <div
        className="no-print"
        style={{
          textAlign: 'right',
          marginBottom: 8,
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        {/* PDF (via print-to-PDF) is only offered for completed batches. */}
        {!header.is_running && (
          <Button icon={<FilePdfOutlined />} onClick={handleDownloadPdf}>
            Download PDF
          </Button>
        )}
        <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
          Print / Download
        </Button>
      </div>

      <div id="bmr-report-root" ref={rootRef} className="bmr-report">
        <div className="container">
          {/* Header */}
          <header
            style={{
              background: tpl.header.bgColor,
              display: 'flex',
              alignItems: 'center',
              padding: '10px 20px',
              marginBottom: 20,
              gap: 12,
            }}
          >
            {/* LEFT — logo + date */}
            <div
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 4,
                minWidth: 80,
              }}
            >
              {(tpl.header.logo || companyLogo) && (
                <img
                  src={tpl.header.logo || resolveUrl(companyLogo)}
                  alt="logo"
                  style={{ height: 40, objectFit: 'contain' }}
                />
              )}
              {header.created_on && (
                <span
                  style={{
                    fontSize: 10,
                    color: tpl.header.textColor,
                    opacity: 0.85,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dt(header.created_on)}
                </span>
              )}
            </div>

            {/* CENTER — title */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <p
                style={{
                  margin: 0,
                  fontSize: tpl.header.fontSize,
                  fontWeight: 'bold',
                  color: tpl.header.textColor,
                }}
              >
                {tpl.header.title || dash(header.report_title)}
                {header.is_running && (
                  <span className="status in-progress" style={{ marginLeft: 8 }}>
                    ⏳ RUNNING
                  </span>
                )}
              </p>
            </div>

            {/* RIGHT — MBMR name flush to right edge */}
            <div style={{ flex: '0 0 auto', textAlign: 'right', minWidth: 80 }}>
              <div
                style={{
                  fontSize: 9,
                  color: tpl.header.textColor,
                  opacity: 0.7,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                {mbmrAlias}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 'bold',
                  color: tpl.header.textColor,
                  wordBreak: 'break-word',
                  maxWidth: 160,
                }}
              >
                {dash(header.mbmr_name)}
              </div>
            </div>
          </header>

          <table className="info-table">
            <colgroup>
              <col style={{ width: '33.33%' }} />
              <col style={{ width: '33.33%' }} />
              <col style={{ width: '33.34%' }} />
            </colgroup>
            <tbody>
              <tr>
                <td>
                  <b>Product Name:</b> {dash(header.product_name)}
                </td>
                <td>
                  <b>{mbmrAlias} Name:</b> {dash(header.mbmr_name)}
                </td>
                <td>
                  <b>BMR:</b> {dash(header.batch_number)}
                </td>
              </tr>
              <tr>
                <td>
                  <b>Created On:</b> {dt(header.created_on)}
                </td>
                <td>
                  <b>Batch Size:</b> {dash(header.batch_size)}
                </td>
                <td>
                  <b>Actual Quantity:</b> {dash(header.actual_quantity)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Process & BMR Time Details */}
          <div className="section-title">
            <table>
              <tbody>
                <tr>
                  <th className="header-class">Process &amp; BMR Time Details</th>
                </tr>
              </tbody>
            </table>
          </div>
          <table>
            <thead>
              <tr className="header-class-1">
                <th style={{ width: '22%' }}>Process Name</th>
                <th style={{ width: '14%' }}>Start Time</th>
                <th style={{ width: '14%' }}>End Time</th>
                <th style={{ width: '12%' }}>Standard Time</th>
                <th style={{ width: '12%' }}>Actual Time</th>
                <th style={{ width: '12%' }}>Difference</th>
                <th style={{ width: '14%' }}>% Deviation</th>
              </tr>
            </thead>
            <tbody>
              {process_time_details.map((p, i) => (
                <tr key={i}>
                  <td>{dash(p.process_name)}</td>
                  <td className="table_data">{dt(p.start_time)}</td>
                  <td className="table_data">{dt(p.end_time)}</td>
                  <td className="table_data">{dash(p.standard_time)}</td>
                  <td className="table_data">{dash(p.actual_time)}</td>
                  <td className="table_data">{dash(p.time_difference)}</td>
                  <td className={`table_data ${sevClass(p.deviation_severity)}`}>
                    {num(p.deviation_percentage)}%
                  </td>
                </tr>
              ))}
              {process_time_totals && Object.keys(process_time_totals).length > 0 && (
                <tr style={{ fontWeight: 'bold', background: '#eaf1fb' }}>
                  <td>Total</td>
                  <td></td>
                  <td></td>
                  <td className="table_data">{dash(process_time_totals.standard_time)}</td>
                  <td className="table_data">{dash(process_time_totals.actual_time)}</td>
                  <td className="table_data">{dash(process_time_totals.time_difference)}</td>
                  <td className="table_data">{num(process_time_totals.deviation_percentage)}%</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* BMR Approvals */}
          <div className="section-title">
            <table>
              <tbody>
                <tr>
                  <th className="header-class">BMR Approvals</th>
                </tr>
              </tbody>
            </table>
          </div>
          <table>
            <thead>
              <tr className="header-class-1">
                <th>Stage</th>
                <th>User</th>
                <th>Date / Time</th>
              </tr>
            </thead>
            <tbody>
              {[
                [`${mbmrAlias} Creation`, bmr_approvals.mbmr_creation],
                [`${mbmrAlias} Approval`, bmr_approvals.mbmr_approval],
                ['BMR Release Approval', bmr_approvals.bmr_release_approval],
                ['BMR Release', bmr_approvals.bmr_release],
              ].map(([label, obj]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{obj ? dash(obj.user) : '—'}</td>
                  <td className="table_data">{obj ? dt(obj.datetime) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Operation Summary */}
          <div className="section-title">
            <table>
              <tbody>
                <tr>
                  <th className="header-class">Operation Summary</th>
                </tr>
              </tbody>
            </table>
          </div>
          <table>
            {/* Column widths tuned to the reference layout: wide Operation,
                narrow Op#/Type/Status, so all 9 columns fit A4 without crop. */}
            <colgroup>
              <col style={{ width: '9%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '26%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr className="header-class-1">
                <th>Process</th>
                <th>Op #</th>
                <th>Operation</th>
                <th>Type</th>
                <th>Status</th>
                <th>Start</th>
                <th>End</th>
                <th>Time Deviation</th>
                <th>Completed By</th>
              </tr>
            </thead>
            <tbody>
              {operation_summary.map((o, i) => (
                <tr key={i}>
                  <td>{dash(o.process)}</td>
                  <td className="table_data">{dash(o.operation_number)}</td>
                  <td>{dash(o.operation_name)}</td>
                  <td>{dash(o.operation_type)}</td>
                  <td className="table_data">
                    <StatusPill status={o.status} />
                  </td>
                  <td className="table_data">{dt(o.start_time)}</td>
                  <td className="table_data">{dt(o.end_time)}</td>
                  <td className="table_data">{dash(o.time_deviation)}</td>
                  <td>{dash(o.completed_by)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Processes -> Operations */}
          {processes.map((proc, pi) => (
            <div key={pi} className="card">
              <h2>Process: {dash(proc.process_name)}</h2>
              <p style={{ margin: '4px 0', color: '#555' }}>
                {(proc.areas || []).length > 0 && (
                  <span>
                    <b>Area:</b> {proc.areas.join(', ')}&nbsp;&nbsp;
                  </span>
                )}
                {(proc.assigned_roles || []).length > 0 && (
                  <span>
                    <b>Roles:</b> {proc.assigned_roles.join(', ')}
                  </span>
                )}
              </p>
              {(proc.operations || []).map((op, oi) => {
                const ed = op.execution_details || {};
                const rm = op.raw_materials || {};
                const rmRows = Array.isArray(rm) ? rm : rm.rows || [];
                const rmTot = (rm && rm.totals) || {};
                return (
                  <div key={oi} style={{ marginBottom: 18 }}>
                    <div className="section-title">
                      <table>
                        <tbody>
                          <tr>
                            <th className="header-class">
                              Op {dash(op.operation_number)}: {dash(op.operation_name)} (
                              {dash(op.operation_type)})
                              {ed.operation_status ? `  —  ${ed.operation_status}` : ''}
                            </th>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <table>
                      <tbody>
                        <tr>
                          <td>
                            <b>Equipment:</b> {dash(ed.machine)}
                          </td>
                          <td>
                            <b>Start:</b> {dt(ed.start_time)}
                          </td>
                          <td>
                            <b>End:</b> {dt(ed.end_time)}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <b>Total Time:</b> {dash(ed.total_time)}
                          </td>
                          <td>
                            <b>Standard:</b> {dash(ed.standard_duration)}
                          </td>
                          <td>
                            <b>Time Deviation:</b> {dash(ed.time_deviation)}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <b>Checked By:</b> {dash(ed.checked_by)}
                          </td>
                          <td colSpan={2}>
                            <b>Completed By:</b> {dash(ed.completed_by)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {rmRows.length > 0 && (
                      <>
                        <div className="section-title" style={{ marginTop: 10 }}>
                          <table>
                            <tbody>
                              <tr>
                                <th className="header-class-1">Raw Materials</th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <table>
                          <colgroup>
                            <col style={{ width: '16%' }} />
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '7%' }} />
                            <col style={{ width: '9%' }} />
                            <col style={{ width: '9%' }} />
                            <col style={{ width: '7%' }} />
                            <col style={{ width: '10%' }} />
                          </colgroup>
                          <thead>
                            <tr className="header-class-1">
                              <th>Equipment</th>
                              <th>Material</th>
                              <th>Addition Time</th>
                              <th>QC No.</th>
                              <th>UOM</th>
                              <th>Standard</th>
                              <th>Actual</th>
                              <th>% Dev</th>
                              <th>Stage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rmRows.map((m, mi) => (
                              <tr key={mi}>
                                <td>{dash(m.equipment)}</td>
                                <td>{dash(m.material)}</td>
                                <td className="table_data">{dt(m.addition_time)}</td>
                                <td className="table_data">{dash(m.quality_control_no)}</td>
                                <td className="table_data">{dash(m.uom)}</td>
                                <td className="table_data">{num(m.standard, 3)}</td>
                                <td className="table_data">{num(m.actual, 3)}</td>
                                <td className={`table_data ${sevClass(m.deviation_severity)}`}>
                                  {num(m.deviation_percentage)}%
                                </td>
                                <td className="table_data">
                                  <StagePill stage={m.stage} />
                                </td>
                              </tr>
                            ))}
                            {Object.keys(rmTot).length > 0 && (
                              <tr style={{ fontWeight: 'bold', background: '#eaf1fb' }}>
                                <td colSpan={5}>Total</td>
                                <td className="table_data">{num(rmTot.standard, 3)}</td>
                                <td className="table_data">{num(rmTot.actual, 3)}</td>
                                <td className="table_data">{num(rmTot.deviation_percentage)}%</td>
                                <td></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </>
                    )}
                    {(op.process_monitoring?.parameters || []).length > 0 && (
                      <>
                        <div className="section-title" style={{ marginTop: 10 }}>
                          <table>
                            <tbody>
                              <tr>
                                <th className="header-class-1">Process Monitoring Details</th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        {op.process_monitoring.parameters.map((p, pmi) => (
                          <table key={pmi} style={{ marginTop: pmi === 0 ? 8 : 4 }}>
                            <colgroup>
                              <col style={{ width: '40%' }} />
                              <col style={{ width: '25%' }} />
                              <col style={{ width: '20%' }} />
                              <col style={{ width: '15%' }} />
                            </colgroup>
                            <thead>
                              <tr className="header-class-1">
                                <th colSpan={4}>
                                  {dash(p.name)} (Spec: {dash(p.min_value)}–{dash(p.max_value)})
                                </th>
                              </tr>
                              <tr className="header-class-1">
                                <th>Addition Time</th>
                                <th>Value</th>
                                <th>Out of Spec</th>
                                <th>Stage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(p.readings || []).length > 0 ? (
                                p.readings.map((r, ri) => (
                                  <tr key={ri}>
                                    <td className="table_data">{dt(r.addition_time)}</td>
                                    <td
                                      className={`table_data ${
                                        r.out_of_range ? 'deviation-critical' : ''
                                      }`}
                                    >
                                      {dash(r.value)}
                                    </td>
                                    <td className="table_data">{r.out_of_range ? 'Yes' : 'No'}</td>
                                    <td className="table_data">
                                      <StagePill stage={p.stage} />
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={4} className="table_data">
                                    No readings recorded
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        ))}
                      </>
                    )}
                    {(op.action_list || []).length > 0 && (
                      <>
                        <div className="section-title" style={{ marginTop: 10 }}>
                          <table>
                            <tbody>
                              <tr>
                                <th className="header-class-1">Action List</th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <table>
                          <thead>
                            <tr className="header-class-1">
                              <th>Label</th>
                              <th>Addition Time</th>
                              <th>Actual Value</th>
                              <th>Stage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {op.action_list.map((a, ai) => (
                              <tr key={ai}>
                                <td>{dash(a.label)}</td>
                                <td className="table_data">{dt(a.addition_time)}</td>
                                <td className="table_data">{dash(a.actual_value)}</td>
                                <td className="table_data">
                                  <StagePill stage={a.stage} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    {(op.quality_details?.parameters || []).length > 0 && (
                      <>
                        <div className="section-title" style={{ marginTop: 10 }}>
                          <table>
                            <tbody>
                              <tr>
                                <th className="header-class-1">
                                  Sampling
                                  {op.quality_details.testing_type
                                    ? ` — ${op.quality_details.testing_type}`
                                    : ''}
                                </th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <table>
                          <thead>
                            <tr className="header-class-1">
                              <th>Parameter</th>
                              <th>Min</th>
                              <th>Max</th>
                              <th>Actual Value</th>
                              <th>Stage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {op.quality_details.parameters.map((s, si) => (
                              <tr key={si}>
                                <td>{dash(s.parameter)}</td>
                                <td className="table_data">{dash(s.min)}</td>
                                <td className="table_data">{dash(s.max)}</td>
                                <td className="table_data">{dash(s.actual_value)}</td>
                                <td className="table_data">
                                  <StagePill stage={s.stage} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    {(op.checklist_items || []).length > 0 && (
                      <>
                        <div className="section-title" style={{ marginTop: 10 }}>
                          <table>
                            <tbody>
                              <tr>
                                <th className="header-class-1">Checklist for BMR Reviewed</th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <table>
                          <thead>
                            <tr className="header-class-1">
                              <th>Checklist</th>
                              <th>Observation</th>
                              <th>Stage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {op.checklist_items.map((c, ci) => (
                              <tr key={ci}>
                                <td>{dash(c.instruction)}</td>
                                <td className="table_data">{c.observation ? '✔ Yes' : '✘ No'}</td>
                                <td className="table_data">
                                  <StagePill stage={c.stage} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    {(op.process_instructions || []).length > 0 && (
                      <>
                        <div className="section-title" style={{ marginTop: 10 }}>
                          <table>
                            <tbody>
                              <tr>
                                <th className="header-class-1">Instructions</th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <table>
                          <thead>
                            <tr className="header-class-1">
                              <th>Note</th>
                              <th>Stage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {op.process_instructions.map((n, ni) => (
                              <tr key={ni}>
                                <td>{dash(n.note)}</td>
                                <td className="table_data">
                                  <StagePill stage={n.stage} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    {(op.packing_details || []).length > 0 && (
                      <>
                        <div className="section-title" style={{ marginTop: 10 }}>
                          <table>
                            <tbody>
                              <tr>
                                <th className="header-class-1">Packing Details</th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <table>
                          <thead>
                            <tr className="header-class-1">
                              <th>Product</th>
                              <th>Container No.</th>
                              <th>Gross Wt.</th>
                              <th>Tare Wt.</th>
                              <th>Net Wt.</th>
                              <th>Stage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {op.packing_details.map((pk, pki) => (
                              <tr key={pki}>
                                <td>{dash(pk.product)}</td>
                                <td className="table_data">{dash(pk.container_number)}</td>
                                <td className="table_data">{num(pk.gross_weight, 3)}</td>
                                <td className="table_data">{num(pk.tare_weight, 3)}</td>
                                <td className="table_data">{num(pk.net_weight, 3)}</td>
                                <td className="table_data">
                                  <StagePill stage={pk.stage} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Material Balance Summary */}
          {material_balance_summary && material_balance_summary.exists && (
            <>
              <div className="section-title">
                <table>
                  <tbody>
                    <tr>
                      <th className="header-class">Material Balance Summary</th>
                    </tr>
                  </tbody>
                </table>
              </div>
              <table>
                <thead>
                  <tr className="header-class-1">
                    <th>Material</th>
                    <th>Unit</th>
                    <th>Reserved</th>
                    <th>Consumed</th>
                    <th>Returned</th>
                    <th>Net Used</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {(material_balance_summary.rows || []).map((r, i) => (
                    <tr key={i}>
                      <td>{dash(r.material_name)}</td>
                      <td className="table_data">{dash(r.unit)}</td>
                      <td className="table_data">{num(r.reserved, 3)}</td>
                      <td className="table_data">{num(r.consumed, 3)}</td>
                      <td className="table_data">{num(r.returned, 3)}</td>
                      <td className="table_data">{num(r.net_used, 3)}</td>
                      <td className="table_data">{num(r.remaining_inventory, 3)}</td>
                    </tr>
                  ))}
                  {material_balance_summary.totals &&
                    Object.keys(material_balance_summary.totals).length > 0 && (
                      <tr style={{ fontWeight: 'bold', background: '#eaf1fb' }}>
                        <td>Total</td>
                        <td></td>
                        <td className="table_data">
                          {num(material_balance_summary.totals.total_reserved, 3)}
                        </td>
                        <td className="table_data">
                          {num(material_balance_summary.totals.total_consumed, 3)}
                        </td>
                        <td className="table_data">
                          {num(material_balance_summary.totals.total_returned, 3)}
                        </td>
                        <td className="table_data">
                          {num(material_balance_summary.totals.total_net_used, 3)}
                        </td>
                        <td className="table_data">
                          {num(material_balance_summary.totals.total_remaining, 3)}
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </>
          )}
        </div>

        <footer
          className="bmr-print-footer"
          style={{
            background: tpl.footer.bgColor,
            color: tpl.footer.textColor,
            fontSize: tpl.footer.fontSize,
          }}
        >
          {tpl.footer.text}
        </footer>
      </div>
    </div>
  );
};

/* Report body CSS. NOTE: the report body/table fonts are FIXED (compact, like
   the reference PDF) and are intentionally NOT derived from the template's
   base_font_size — that template size controls ONLY the header text (applied
   inline via tpl.header.fontSize on the <header>). This keeps a large header
   font from blowing up the whole report. */
const getReportStyle = () => `<style>
  .bmr-report, .bmr-report * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  * { box-sizing: border-box; }
  /* On screen: fill the available panel width (no fixed width -> no horizontal
     scroll, no wasted left/right space). In print (@media print below): pin to
     the exact A4 content width so the PDF fits the page. table-layout:fixed +
     word-wrap keep every table inside whichever width applies. */
  .bmr-report { font-family: Arial, "Helvetica Neue", Helvetica, sans-serif; font-size: 11px; line-height: 1.3; color: #2c3e50; background:#fff; padding: 0; width: 100%; max-width: 100%; margin: 0; }
  .bmr-report .container { width: 100%; max-width: 100%; margin: 0; }
  .bmr-report header { margin: 0; padding: 0; }
  .bmr-print-footer { display: none; }
  .bmr-report .card { border: 1px solid #d0d7de; border-radius: 6px; padding: 10px;
    margin-bottom: 20px; background:#fff; box-shadow: 0 2px 5px rgba(0,0,0,0.05);
    width: 100%; max-width: 100%; overflow: hidden; }
  .bmr-report .card h2 { margin-top: 0; font-size: 14px; color: #1a73e8;
    border-bottom: 2px solid #e5e9f0; padding-bottom: 8px; }
  /* table-layout:fixed + width:100% keep tables inside the page width (no right
     crop); word-break lets long codes/timestamps wrap (no slant/overflow).
     overflow:hidden clips any residual pixel that a long unbreakable token would
     otherwise push past the column edge. */
  .bmr-report table { width: 100%; max-width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 9.5px; table-layout: fixed; }
  /* break-word (not "anywhere") only splits a token when it genuinely can't fit,
     so normal words like "EXECUTION" stay on one line instead of breaking into
     one letter per row. Long unbroken codes still wrap. */
  .bmr-report th, .bmr-report td { border: 1px solid #d0d7de; padding: 4px 5px; text-align: left; vertical-align: top; overflow-wrap: break-word; word-break: normal; white-space: normal; overflow: hidden; }
  .bmr-report th { background-color: #eaf1fb; color: #1a1a1a; text-align: center; }
  .bmr-report tr:nth-child(even) { background-color: #f9fbfd; }
  .bmr-report .table_data { text-align: center; }
  .bmr-report .header-class { background-color: #204448 !important; color: #fff !important; }
  .bmr-report .header-class-1 th, .bmr-report tr.header-class-1 th { background-color: #8FB1B5 !important; color: #fff !important; }
  .bmr-report .header-class-1 { background-color: #8FB1B5 !important; color: #fff !important; }
  .bmr-report .status { font-weight: bold; padding: 3px 6px; border-radius: 4px; font-size: 9px; display: inline-block; white-space: nowrap; }
  .bmr-report .completed { background-color: #d1f7d6; color: #1b5e20; }
  .bmr-report .in-progress { background-color: #fff3cd; color: #856404; }
  .bmr-report .pending { background-color: #f0f0f0; color: #555; }
  .bmr-report .rejected { background-color: #f8d7da; color: #721c24; }
  .bmr-report .deviation-critical { background-color: #ffcccc; color: #cc0000; font-weight: bold; }
  .bmr-report .deviation-moderate { background-color: #ffe6cc; color: #cc6600; font-weight: bold; }
  .bmr-report .deviation-minor { background-color: #fff9cc; color: #ccaa00; }
  .bmr-report .section-title { margin-top: 18px; }
  .bmr-report .section-title table { margin-top: 0; }
  /* nowrap so "EXECUTION" / "PRE" / "POST" stay on ONE line inside their column
     instead of breaking one letter per row. */
  .bmr-report .pill { display: inline-block; padding: 2px 6px; border-radius: 10px; font-size: 9px; font-weight: bold; white-space: nowrap; }
  .bmr-report .pill-action { background-color: #e0e8ff; color: #1a3a8a; border: 1px solid #8aa0e0; }
  .bmr-report .pill-dev { background-color: #d5f5e3; color: #1e8449; border: 1px solid #82c99e; }
  .bmr-report .pill-muted { background-color: #fdebd0; color: #9c640c; border: 1px solid #e0bd80; }
  @media print {
    .no-print { display: none !important; }
    html, body, .bmr-report, .bmr-report * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    /* A4 portrait, margins on @page = 0. The report is pinned to the A4 content
       width (186mm) by the print-window override injected in handlePrint(), so
       it fits the page in both "Print" and "Save as PDF". Bottom padding on body
       reserves room for the fixed footer. */
    @page { size: A4 portrait; margin: 12mm; }
    html, body { margin: 0; padding: 0 0 10mm 0; }
    .bmr-report table { max-width: 186mm; }
    /* Don't split a row across pages; repeat the table header on each page. */
    .bmr-report tr { page-break-inside: avoid; }
    .bmr-report thead { display: table-header-group; }
    .bmr-report table { page-break-inside: auto; }
    .bmr-print-footer {
      display: block;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 4px 0;
      border-top: 1px solid #ccc;
      text-align: center;
      font-size: 9px;
      color: #555;
    }
  }
</style>`;

export default BatchDetailedReport;
